import { Box, Typography } from '@mui/material';
import { useEffect, useState, useRef } from 'react';

interface MetricCardProps {
  value: string;
  label: string;
  description?: string;
  tagline?: string;
  align?: 'left' | 'center' | 'right';
  valueColor?: string;
}

function useCountUp(end: number, duration: number = 2000, startOnView: boolean = true) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasStarted, startOnView]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime: number | null = null;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth deceleration
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, hasStarted]);

  return { count, ref };
}

function parseValue(value: string): { number: number; prefix: string; suffix: string } {
  // Remove commas for parsing
  const cleanValue = value.replace(/,/g, '');
  const match = cleanValue.match(/^([^\d]*)(\d+)(.*)$/);
  if (match) {
    return {
      prefix: match[1],
      number: parseInt(match[2], 10),
      suffix: match[3],
    };
  }
  return { prefix: '', number: 0, suffix: value };
}

export function MetricCard({
  value,
  label,
  description,
  tagline,
  align = 'left',
  valueColor = 'text.primary'
}: MetricCardProps) {
  const { prefix, number, suffix } = parseValue(value);
  const { count, ref } = useCountUp(number, 2000);

  // Format count with commas if the original value had them (simple heuristic: if we parsed a number > 999)
  const formattedCount = count.toLocaleString();

  return (
    <Box ref={ref} sx={{ textAlign: align }}>
      {tagline && (
        <Typography
          variant="overline"
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            letterSpacing: 1,
            display: 'block',
            mb: 0.5,
          }}
        >
          {tagline}
        </Typography>
      )}
      <Typography
        sx={{
          fontSize: { xs: '3rem', md: '3.5rem' },
          fontWeight: 700,
          lineHeight: 1,
          color: valueColor,
          mb: 1,
        }}
      >
        {prefix}{formattedCount}{suffix}
      </Typography>
      <Typography
        variant="h6"
        component="p"
        sx={{
          mb: 0.5,
          fontWeight: 600,
          color: valueColor === 'white' ? 'rgba(255,255,255,0.8)' : 'text.primary'
        }}
      >
        {label}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{
            color: valueColor === 'white' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}
